/**
 * Модель документа: абзацы с вычисленным форматированием, видимым текстом
 * и привязкой к XML-узлам, таблицы, разделы, колонтитулы.
 *
 * Текст абзаца — то, что видит читатель при принятых исправлениях:
 * результаты полей включены, коды полей и удалённый текст — нет.
 * Встроенные объекты (рисунки, формулы) обозначаются символом U+FFFC.
 */

import { DocxError } from '../errors';
import { Rel, type DocxPackage } from '../ooxml/package';
import {
  childElements,
  childVal,
  descendants,
  firstChild,
  textContent,
  type XElement,
  type XText,
} from '../ooxml/xml';
import { OBJ } from '../util/text';
import { emuToMm, parseIntAttr } from '../util/units';
import { Numbering, NumberingCounter, mapSymbolChar, type ListLabel } from './numbering';
import { parsePPr, parseRPr, type Align, type Origin, type ParaProps, type RunProps } from './props';
import { parseSection, type Section } from './sections';
import { StyleSheet } from './styles';
import { parseTheme } from './theme';

export interface TextSegment {
  start: number;
  end: number;
  /** Текстовый узел внутри w:t — по нему исправления меняют текст. */
  node?: XText;
  /** Табуляция w:tab вместо текста: исправление может только удалить её целиком. */
  tab?: XElement;
  run: XElement;
  /** false для результатов полей и уже существующих исправлений: их не трогаем. */
  editable: boolean;
}

export interface RunInfo {
  el: XElement;
  start: number;
  end: number;
  props: RunProps;
  origin: Partial<Record<keyof RunProps, Origin>>;
  styleId?: string;
  inField: boolean;
  inHyperlink: boolean;
  inserted: boolean;
}

export interface FieldInfo {
  /** Первое слово кода поля в верхнем регистре: PAGE, TOC, SEQ, REF… */
  type: string;
  instr: string;
}

export type ObjectKind = 'image' | 'chart' | 'diagram' | 'group' | 'shape' | 'textbox' | 'ole' | 'equation';

export interface ObjectInfo {
  kind: ObjectKind;
  inline: boolean;
  widthMm?: number;
  heightMm?: number;
}

export interface CellRef {
  table: number;
  row: number;
  col: number;
}

export interface Paragraph {
  kind: 'paragraph';
  /** Сквозной номер абзаца в документе (включая абзацы в таблицах). */
  index: number;
  el: XElement;
  part: string;
  /** Итоговый стиль абзаца (явный или стиль по умолчанию). */
  styleId?: string;
  direct: ParaProps;
  props: ParaProps;
  origin: Partial<Record<keyof ParaProps, Origin>>;
  runs: RunInfo[];
  segments: TextSegment[];
  text: string;
  list?: ListLabel;
  cell?: CellRef;
  textBox: boolean;
  section: number;
  objects: ObjectInfo[];
  math?: { display: boolean; text: string };
  fields: FieldInfo[];
  inToc: boolean;
  /** Абзац начинается с новой страницы за счёт жёсткого разрыва (не случайно). */
  pageBreakBefore: boolean;
  startsWithBreak: boolean;
  /** После всего видимого содержимого стоит разрыв страницы. */
  breakAfter: boolean;
  lineBreaks: number;
  isEmpty: boolean;
  bookmarks: string[];
  /** При последней отрисовке в Word абзац оказался в начале страницы. */
  renderedPageStart: boolean;
  /** Сколько раз в абзаце начиналась новая страница при последней отрисовке в Word (w:lastRenderedPageBreak). */
  renderedBreaks: number;
  footnoteRefs: number;
  hasSectPr: boolean;
}

export interface TableCell {
  blocks: Block[];
}

export interface Table {
  kind: 'table';
  index: number;
  el: XElement;
  rows: Array<{ cells: TableCell[] }>;
  styleId?: string;
  bordered: boolean;
  nested: boolean;
  firstParagraph: number;
  lastParagraph: number;
}

export type Block = Paragraph | Table;

export interface HeaderFooter {
  part: string;
  kind: 'header' | 'footer';
  paragraphs: Paragraph[];
  /** Абзац с полем PAGE (номер страницы), если есть. */
  pageField?: { paragraph: Paragraph; align: Align | 'unknown'; inTextBox: boolean };
  hasContent: boolean;
}

export interface DocSettings {
  evenAndOddHeaders: boolean;
  mirrorMargins: boolean;
  gutterAtTop: boolean;
  trackRevisions: boolean;
  autoHyphenation: boolean;
}

export interface DocMeta {
  title?: string;
  creator?: string;
  lastModifiedBy?: string;
  created?: string;
  modified?: string;
  application?: string;
  company?: string;
  pages?: number;
  words?: number;
  characters?: number;
  totalEditMinutes?: number;
}

export interface DocModel {
  pkg: DocxPackage;
  mainPart: string;
  styles: StyleSheet;
  numbering: Numbering;
  blocks: Block[];
  /** Все абзацы основного текста по порядку, включая абзацы в таблицах. */
  paragraphs: Paragraph[];
  /** Абзацы надписей (текстовых полей) — вне основного потока. */
  textBoxes: Paragraph[];
  tables: Table[];
  sections: Section[];
  headersFooters: Map<string, HeaderFooter>;
  settings: DocSettings;
  meta: DocMeta;
  /** Количество непринятых исправлений (вставки, удаления, изменения формата). */
  revisions: number;
  comments: number;
  footnotes: number;
  altChunks: number;
}

interface FieldFrame {
  instr: string;
  type: string;
  phase: 'instr' | 'result';
}

interface InlineCtx {
  inHyperlink: boolean;
  inserted: boolean;
}

export function fieldType(instr: string): string {
  const m = /^\s*([A-Za-z]+)/.exec(instr);
  return m ? m[1]!.toUpperCase() : '';
}

const REVISION_ELEMENTS = new Set([
  'w:ins', 'w:del', 'w:moveFrom', 'w:moveTo', 'w:rPrChange', 'w:pPrChange', 'w:sectPrChange',
  'w:tblPrChange', 'w:trPrChange', 'w:tcPrChange', 'w:numberingChange', 'w:cellIns', 'w:cellDel',
]);

function countRevisions(root: XElement): number {
  let n = 0;
  const walk = (e: XElement): void => {
    for (const c of e.children) {
      if (c.type !== 'element') continue;
      if (REVISION_ELEMENTS.has(c.name)) n++;
      walk(c);
    }
  };
  walk(root);
  return n;
}

function hasVisibleBorder(borders: XElement): boolean {
  return childElements(borders).some((b) => {
    const v = b.attrs['w:val'];
    return !!v && v !== 'nil' && v !== 'none';
  });
}

/** Размер VML-фигуры из атрибута style="width:120pt;height:40pt". */
function vmlSize(style: string | undefined): { widthMm?: number; heightMm?: number } {
  if (!style) return {};
  const get = (prop: string): number | undefined => {
    const m = new RegExp(`${prop}\\s*:\\s*([\\d.]+)(pt|in|mm|cm|px)?`).exec(style);
    if (!m) return undefined;
    const v = parseFloat(m[1]!);
    switch (m[2]) {
      case 'in':
        return v * 25.4;
      case 'mm':
        return v;
      case 'cm':
        return v * 10;
      case 'px':
        return (v * 25.4) / 96;
      default:
        return (v * 25.4) / 72;
    }
  };
  return { widthMm: get('width'), heightMm: get('height') };
}

class Walker {
  readonly paragraphs: Paragraph[] = [];
  readonly tables: Table[] = [];
  readonly pendingTextBoxes: XElement[] = [];
  readonly sectionEnds: Array<{ el: XElement; lastParagraph: number }> = [];
  finalSectPr?: XElement;
  altChunks = 0;

  private fieldStack: FieldFrame[] = [];
  private sdtGalleries: string[] = [];
  private nextIndex: number;

  // Состояние текущего абзаца.
  private text = '';
  private segments: TextSegment[] = [];
  private hasVisible = false;
  private breakPos = -1;
  private breakObjects = 0;

  constructor(
    private readonly part: string,
    private readonly styles: StyleSheet,
    private readonly numbering: Numbering,
    private readonly counter: NumberingCounter,
    startIndex = 0,
  ) {
    this.nextIndex = startIndex;
  }

  get indexCursor(): number {
    return this.nextIndex;
  }

  walkBlocks(container: XElement, out: Block[], cell?: CellRef, textBox = false): void {
    for (const c of container.children) {
      if (c.type !== 'element') continue;
      switch (c.name) {
        case 'w:p':
          out.push(this.paragraph(c, cell, textBox));
          break;
        case 'w:tbl':
          out.push(this.table(c, cell, textBox));
          break;
        case 'w:sdt': {
          const content = firstChild(c, 'w:sdtContent');
          if (!content) break;
          const pr = firstChild(c, 'w:sdtPr');
          const gallery = pr ? descendants(pr, 'w:docPartGallery')[0]?.attrs['w:val'] : undefined;
          this.sdtGalleries.push(gallery ?? '');
          this.walkBlocks(content, out, cell, textBox);
          this.sdtGalleries.pop();
          break;
        }
        case 'w:customXml':
          this.walkBlocks(c, out, cell, textBox);
          break;
        case 'mc:AlternateContent': {
          const choice = firstChild(c, 'mc:Choice') ?? firstChild(c, 'mc:Fallback');
          if (choice) this.walkBlocks(choice, out, cell, textBox);
          break;
        }
        case 'w:sectPr':
          if (!cell && !textBox) this.finalSectPr = c;
          break;
        case 'w:altChunk':
          this.altChunks++;
          break;
      }
    }
  }

  private table(tbl: XElement, parentCell: CellRef | undefined, textBox: boolean): Table {
    const tblPr = firstChild(tbl, 'w:tblPr');
    const styleId = tblPr ? childVal(tblPr, 'w:tblStyle') : undefined;
    const t: Table = {
      kind: 'table',
      index: this.tables.length,
      el: tbl,
      rows: [],
      styleId,
      bordered: this.tableBordered(tblPr, styleId, tbl),
      nested: !!parentCell,
      firstParagraph: this.nextIndex,
      lastParagraph: this.nextIndex - 1,
    };
    this.tables.push(t);
    const rows: XElement[] = [];
    const collectRows = (e: XElement): void => {
      for (const c of childElements(e)) {
        if (c.name === 'w:tr') rows.push(c);
        else if (c.name === 'w:sdt') {
          const content = firstChild(c, 'w:sdtContent');
          if (content) collectRows(content);
        } else if (c.name === 'w:customXml') collectRows(c);
      }
    };
    collectRows(tbl);
    rows.forEach((tr, r) => {
      const cells: XElement[] = [];
      const collectCells = (e: XElement): void => {
        for (const c of childElements(e)) {
          if (c.name === 'w:tc') cells.push(c);
          else if (c.name === 'w:sdt') {
            const content = firstChild(c, 'w:sdtContent');
            if (content) collectCells(content);
          } else if (c.name === 'w:customXml') collectCells(c);
        }
      };
      collectCells(tr);
      t.rows.push({
        cells: cells.map((tc, col) => {
          const cell: TableCell = { blocks: [] };
          this.walkBlocks(tc, cell.blocks, { table: t.index, row: r, col }, textBox);
          return cell;
        }),
      });
    });
    t.lastParagraph = this.nextIndex - 1;
    return t;
  }

  private tableBordered(tblPr: XElement | undefined, styleId: string | undefined, tbl: XElement): boolean {
    const own = tblPr && firstChild(tblPr, 'w:tblBorders');
    if (own) return hasVisibleBorder(own);
    if (styleId) {
      for (const s of this.styles.chain(styleId).reverse()) {
        const sp = firstChild(s.el, 'w:tblPr');
        const b = sp && firstChild(sp, 'w:tblBorders');
        if (b) return hasVisibleBorder(b);
      }
    }
    const cellBorders = descendants(tbl, 'w:tcBorders')[0];
    return cellBorders ? hasVisibleBorder(cellBorders) : false;
  }

  private paragraph(p: XElement, cell: CellRef | undefined, textBox: boolean): Paragraph {
    const pPrEl = firstChild(p, 'w:pPr');
    const direct = parsePPr(pPrEl);
    const explicitStyle = pPrEl ? childVal(pPrEl, 'w:pStyle') : undefined;
    const styleId =
      explicitStyle && this.styles.get(explicitStyle) ? explicitStyle : this.styles.defaultParagraphStyle;

    // Нумерация: прямая или унаследованная от стиля; numId=0 отменяет нумерацию стиля.
    const styleProps = this.styles.paragraphStyle(styleId).pPr.props;
    const numId = direct.numId ?? styleProps.numId;
    const fromStyle = direct.numId === undefined && styleProps.numId !== undefined;
    let ilvl = direct.ilvl ?? styleProps.ilvl;
    if (numId !== undefined && ilvl === undefined && styleId) ilvl = this.numbering.levelForStyle(numId, styleId);
    ilvl ??= 0;
    let list: ListLabel | undefined;
    let numPPr: ParaProps | undefined;
    if (numId && this.numbering.has(numId)) {
      numPPr = this.numbering.level(numId, ilvl)?.pPr;
      list = this.counter.next(numId, ilvl);
    }
    const resolved = this.styles.resolveParagraph(styleId, direct, numPPr ? { pPr: numPPr, fromStyle } : undefined);

    const para: Paragraph = {
      kind: 'paragraph',
      index: this.nextIndex++,
      el: p,
      part: this.part,
      styleId,
      direct,
      props: resolved.props,
      origin: resolved.origin,
      runs: [],
      segments: [],
      text: '',
      list,
      cell,
      textBox,
      section: 0,
      objects: [],
      fields: [],
      inToc: this.sdtGalleries.some((g) => /table of contents/i.test(g)) || this.inTocField(),
      pageBreakBefore: false,
      startsWithBreak: false,
      breakAfter: false,
      lineBreaks: 0,
      isEmpty: true,
      bookmarks: [],
      renderedPageStart: false,
      renderedBreaks: 0,
      footnoteRefs: 0,
      hasSectPr: !!(pPrEl && firstChild(pPrEl, 'w:sectPr')),
    };

    this.text = '';
    this.segments = para.segments;
    this.hasVisible = false;
    this.breakPos = -1;
    this.breakObjects = 0;
    this.walkInline(p, para, { inHyperlink: false, inserted: false });
    para.text = this.text;

    if (this.breakPos >= 0) {
      para.breakAfter = this.text.slice(this.breakPos).trim() === '' && para.objects.length === this.breakObjects;
    }
    para.isEmpty = this.text.replace(/[\s\u00ad]/g, '').replace(/[\u200b-\u200d\u2060\ufeff]/g, '') === '' && para.objects.length === 0 && !para.math;
    if (para.math && !para.math.display) {
      const rest = this.text.replace(/\ufffc/g, '').replace(/\(\s*[А-ЯA-Z]?\.?\d+(?:[.,]\d+)*\s*\)/, '');
      if (rest.trim() === '') para.math.display = true;
    }
    if (para.fields.some((f) => f.type === 'TOC') || this.styles.isTocStyle(styleId)) para.inToc = true;
    if (para.props.indFirstChars !== undefined) {
      const sz = para.runs[0]?.props.sz ?? this.styles.defaultsR.sz ?? 20;
      para.props.indFirst = Math.round((para.props.indFirstChars / 100) * sz * 10);
    }
    if (para.hasSectPr && !cell && !textBox) {
      this.sectionEnds.push({ el: firstChild(pPrEl!, 'w:sectPr')!, lastParagraph: para.index });
    }
    this.paragraphs.push(para);
    return para;
  }

  private inTocField(): boolean {
    return this.fieldStack.some((f) => f.type === 'TOC' && f.phase === 'result');
  }

  private visible(): boolean {
    for (const f of this.fieldStack) if (f.phase === 'instr') return false;
    return true;
  }

  private append(s: string, seg?: Omit<TextSegment, 'start' | 'end'>): void {
    if (seg) this.segments.push({ ...seg, start: this.text.length, end: this.text.length + s.length });
    this.text += s;
    if (s.trim() !== '') this.hasVisible = true;
  }

  private walkInline(container: XElement, para: Paragraph, ctx: InlineCtx): void {
    for (const c of container.children) {
      if (c.type !== 'element') continue;
      switch (c.name) {
        case 'w:r':
          this.run(c, para, ctx);
          break;
        case 'w:hyperlink':
          this.walkInline(c, para, { ...ctx, inHyperlink: true });
          break;
        case 'w:ins':
        case 'w:moveTo':
          this.walkInline(c, para, { ...ctx, inserted: true });
          break;
        case 'w:smartTag':
        case 'w:customXml':
        case 'w:dir':
        case 'w:bdo':
          this.walkInline(c, para, ctx);
          break;
        case 'w:sdt': {
          const content = firstChild(c, 'w:sdtContent');
          if (content) this.walkInline(content, para, ctx);
          break;
        }
        case 'w:fldSimple': {
          const instr = c.attrs['w:instr'] ?? '';
          const frame: FieldFrame = { instr, type: fieldType(instr), phase: 'result' };
          para.fields.push({ type: frame.type, instr: instr.trim() });
          if (frame.type === 'TOC') para.inToc = true;
          this.fieldStack.push(frame);
          this.walkInline(c, para, ctx);
          this.fieldStack.pop();
          break;
        }
        case 'm:oMathPara':
        case 'm:oMath':
          this.math(c, para);
          break;
        case 'w:bookmarkStart': {
          const name = c.attrs['w:name'];
          if (name) para.bookmarks.push(name);
          break;
        }
        case 'mc:AlternateContent': {
          const choice = firstChild(c, 'mc:Choice') ?? firstChild(c, 'mc:Fallback');
          if (choice) this.walkInline(choice, para, ctx);
          break;
        }
        // w:del, w:moveFrom — удалённое содержимое не показываем.
      }
    }
  }

  private run(r: XElement, para: Paragraph, ctx: InlineCtx): void {
    const rPrEl = firstChild(r, 'w:rPr');
    const styleId = rPrEl ? childVal(rPrEl, 'w:rStyle') : undefined;
    const resolved = this.styles.resolveRun(para.styleId, styleId, parseRPr(rPrEl));
    const start = this.text.length;
    const inFieldAtStart = this.fieldStack.length > 0;
    this.runContent(r, r, para, ctx);
    const end = this.text.length;
    if (end > start) {
      para.runs.push({
        el: r,
        start,
        end,
        props: resolved.props,
        origin: resolved.origin,
        styleId,
        inField: inFieldAtStart || this.fieldStack.length > 0,
        inHyperlink: ctx.inHyperlink,
        inserted: ctx.inserted,
      });
    }
  }

  private runContent(container: XElement, r: XElement, para: Paragraph, ctx: InlineCtx): void {
    for (const c of container.children) {
      if (c.type !== 'element') continue;
      switch (c.name) {
        case 'w:t': {
          if (!this.visible()) break;
          if (this.inTocField()) para.inToc = true;
          const editable = this.fieldStack.length === 0 && !ctx.inserted;
          for (const tn of c.children) {
            if (tn.type === 'text' && tn.value) this.append(tn.value, { node: tn, run: r, editable });
          }
          break;
        }
        case 'w:instrText': {
          const top = this.fieldStack[this.fieldStack.length - 1];
          if (top && top.phase === 'instr') top.instr += textContent(c);
          break;
        }
        case 'w:fldChar': {
          const t = c.attrs['w:fldCharType'];
          if (t === 'begin') {
            this.fieldStack.push({ instr: '', type: '', phase: 'instr' });
          } else if (t === 'separate') {
            const top = this.fieldStack[this.fieldStack.length - 1];
            if (top && top.phase === 'instr') {
              top.phase = 'result';
              top.type = fieldType(top.instr);
              para.fields.push({ type: top.type, instr: top.instr.trim() });
              if (top.type === 'TOC') para.inToc = true;
            }
          } else if (t === 'end') {
            const top = this.fieldStack.pop();
            if (top && top.phase === 'instr') {
              top.type = fieldType(top.instr);
              para.fields.push({ type: top.type, instr: top.instr.trim() });
            }
          }
          break;
        }
        case 'w:tab':
          if (this.visible()) this.append('\t', { tab: c, run: r, editable: this.fieldStack.length === 0 && !ctx.inserted });
          break;
        case 'w:ptab':
          if (this.visible()) this.append('\t');
          break;
        case 'w:br': {
          if (!this.visible()) break;
          const type = c.attrs['w:type'];
          if (type === 'page') {
            if (!this.hasVisible && para.objects.length === 0) para.startsWithBreak = true;
            this.breakPos = this.text.length;
            this.breakObjects = para.objects.length;
          } else {
            if (type !== 'column') para.lineBreaks++;
            this.append('\n');
          }
          break;
        }
        case 'w:cr':
          if (this.visible()) {
            para.lineBreaks++;
            this.append('\n');
          }
          break;
        case 'w:noBreakHyphen':
          if (this.visible()) this.append('\u2011');
          break;
        case 'w:softHyphen':
          if (this.visible()) this.append('\u00ad');
          break;
        case 'w:sym': {
          if (!this.visible()) break;
          const code = parseInt(c.attrs['w:char'] ?? '', 16);
          if (code) this.append(mapSymbolChar(String.fromCharCode(code)));
          break;
        }
        case 'w:drawing':
          this.drawing(c, para);
          break;
        case 'w:pict':
          this.vml(c, para);
          break;
        case 'w:object':
          this.oleObject(c, para);
          break;
        case 'mc:AlternateContent': {
          const choice = firstChild(c, 'mc:Choice') ?? firstChild(c, 'mc:Fallback');
          if (choice) this.runContent(choice, r, para, ctx);
          break;
        }
        case 'w:footnoteReference':
        case 'w:endnoteReference':
          para.footnoteRefs++;
          break;
        case 'w:lastRenderedPageBreak':
          if (!this.hasVisible && para.objects.length === 0) para.renderedPageStart = true;
          para.renderedBreaks++;
          break;
      }
    }
  }

  private drawing(d: XElement, para: Paragraph): void {
    const inline = firstChild(d, 'wp:inline');
    const container = inline ?? firstChild(d, 'wp:anchor');
    if (!container) return;
    const extent = firstChild(container, 'wp:extent');
    const cx = parseIntAttr(extent?.attrs['cx']);
    const cy = parseIntAttr(extent?.attrs['cy']);
    const uri = descendants(container, 'a:graphicData')[0]?.attrs['uri'] ?? '';
    const textBoxes = descendants(container, 'w:txbxContent');
    let kind: ObjectKind = 'shape';
    if (uri.endsWith('/picture')) kind = 'image';
    else if (uri.endsWith('/chart') || uri.includes('chartex')) kind = 'chart';
    else if (uri.endsWith('/diagram')) kind = 'diagram';
    else if (uri.includes('wordprocessingGroup') || uri.includes('wordprocessingCanvas')) kind = 'group';
    else if (textBoxes.length) kind = 'textbox';
    this.pendingTextBoxes.push(...textBoxes);
    para.objects.push({
      kind,
      inline: !!inline,
      widthMm: cx !== undefined ? emuToMm(cx) : undefined,
      heightMm: cy !== undefined ? emuToMm(cy) : undefined,
    });
    if (inline && this.visible()) this.append(OBJ);
  }

  private vml(pict: XElement, para: Paragraph): void {
    const shape = descendants(pict, 'v:shape')[0] ?? descendants(pict, 'v:rect')[0];
    const style = shape?.attrs['style'];
    const textBoxes = descendants(pict, 'w:txbxContent');
    const kind: ObjectKind = descendants(pict, 'v:imagedata').length ? 'image' : textBoxes.length ? 'textbox' : 'shape';
    this.pendingTextBoxes.push(...textBoxes);
    const inline = !style || !/position\s*:\s*absolute/.test(style);
    para.objects.push({ kind, inline, ...vmlSize(style) });
    if (inline && this.visible()) this.append(OBJ);
  }

  private oleObject(o: XElement, para: Paragraph): void {
    const progId = descendants(o, 'o:OLEObject')[0]?.attrs['ProgID'] ?? '';
    const isEquation = /^(Equation|MathType)|DSMT/i.test(progId);
    const shape = descendants(o, 'v:shape')[0];
    para.objects.push({ kind: isEquation ? 'equation' : 'ole', inline: true, ...vmlSize(shape?.attrs['style']) });
    if (isEquation) para.math = { display: false, text: para.math?.text ?? '' };
    if (this.visible()) this.append(OBJ);
  }

  private math(m: XElement, para: Paragraph): void {
    const text = descendants(m, 'm:t').map(textContent).join('');
    para.math = { display: m.name === 'm:oMathPara' || !!para.math?.display, text: (para.math?.text ?? '') + text };
    if (this.visible()) this.append(OBJ);
  }
}

function readSettings(doc: import('../ooxml/xml').XDocument | undefined): DocSettings {
  const on = (name: string): boolean => {
    const e = doc && firstChild(doc.root, name);
    return !!e && e.attrs['w:val'] !== '0' && e.attrs['w:val'] !== 'false';
  };
  return {
    evenAndOddHeaders: on('w:evenAndOddHeaders'),
    mirrorMargins: on('w:mirrorMargins'),
    gutterAtTop: on('w:gutterAtTop'),
    trackRevisions: on('w:trackRevisions'),
    autoHyphenation: on('w:autoHyphenation'),
  };
}

function readMeta(pkg: DocxPackage): DocMeta {
  const meta: DocMeta = {};
  const rootRels = pkg.rels('');
  const core = rootRels.find((r) => r.type.endsWith(Rel.coreProps));
  const coreDoc = core ? pkg.getXml(core.target) : pkg.getXml('docProps/core.xml');
  if (coreDoc) {
    const get = (name: string): string | undefined => {
      const e = firstChild(coreDoc.root, name);
      const v = e ? textContent(e).trim() : '';
      return v || undefined;
    };
    meta.title = get('dc:title');
    meta.creator = get('dc:creator');
    meta.lastModifiedBy = get('cp:lastModifiedBy');
    meta.created = get('dcterms:created');
    meta.modified = get('dcterms:modified');
  }
  const app = rootRels.find((r) => r.type.endsWith(Rel.extendedProps));
  const appDoc = app ? pkg.getXml(app.target) : pkg.getXml('docProps/app.xml');
  if (appDoc) {
    const get = (name: string): string | undefined => {
      const e = childElements(appDoc.root).find((c) => c.name === name || c.name.endsWith(':' + name));
      const v = e ? textContent(e).trim() : '';
      return v || undefined;
    };
    meta.application = get('Application');
    meta.company = get('Company');
    meta.pages = parseIntAttr(get('Pages'));
    meta.words = parseIntAttr(get('Words'));
    meta.characters = parseIntAttr(get('Characters'));
    meta.totalEditMinutes = parseIntAttr(get('TotalTime'));
  }
  return meta;
}

function countChildren(doc: import('../ooxml/xml').XDocument | undefined, name: string, skipSpecial: boolean): number {
  if (!doc) return 0;
  return childElements(doc.root, name).filter((e) => !skipSpecial || !e.attrs['w:type'] || e.attrs['w:type'] === 'normal')
    .length;
}

export function buildModel(pkg: DocxPackage): DocModel {
  const mainPart = pkg.mainPart;
  const docXml = pkg.getXml(mainPart);
  if (!docXml || docXml.root.name !== 'w:document') throw new DocxError('not-docx');
  const body = firstChild(docXml.root, 'w:body');
  if (!body) throw new DocxError('corrupt', 'нет w:body');

  const part = (rel: string): import('../ooxml/xml').XDocument | undefined => {
    const p = pkg.relatedPart(mainPart, rel);
    return p ? pkg.getXml(p) : undefined;
  };

  const theme = parseTheme(part(Rel.theme));
  const styles = new StyleSheet(part(Rel.styles), theme);
  const numbering = new Numbering(part(Rel.numbering), styles);

  const walker = new Walker(mainPart, styles, numbering, new NumberingCounter(numbering));
  const blocks: Block[] = [];
  walker.walkBlocks(body, blocks);
  const paragraphs = walker.paragraphs.slice();

  // Надписи обрабатываем после основного текста, чтобы не сбивать сквозную нумерацию абзацев.
  const textBoxes: Paragraph[] = [];
  for (let i = 0; i < walker.pendingTextBoxes.length; i++) {
    const before = walker.paragraphs.length;
    walker.walkBlocks(walker.pendingTextBoxes[i]!, [], undefined, true);
    textBoxes.push(...walker.paragraphs.slice(before));
  }

  // Разделы.
  const sectionEls = walker.sectionEnds.slice();
  if (walker.finalSectPr) sectionEls.push({ el: walker.finalSectPr, lastParagraph: paragraphs.length - 1 });
  const sections: Section[] = [];
  let first = 0;
  for (const s of sectionEls) {
    const sec = parseSection(s.el, sections.length, pkg, mainPart, sections[sections.length - 1]);
    sec.firstParagraph = first;
    sec.lastParagraph = s.lastParagraph;
    first = s.lastParagraph + 1;
    sections.push(sec);
  }
  let si = 0;
  for (const p of paragraphs) {
    while (si < sections.length - 1 && p.index > sections[si]!.lastParagraph) si++;
    p.section = si;
  }

  // Жёсткие разрывы страниц перед абзацами.
  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i]!;
    const prev = i > 0 ? paragraphs[i - 1] : undefined;
    let br = !!p.props.pageBreakBefore || p.startsWithBreak;
    if (prev?.breakAfter) br = true;
    if (prev?.hasSectPr && p.section > 0 && sections[p.section]?.type !== 'continuous') br = true;
    p.pageBreakBefore = br;
  }

  // Колонтитулы.
  const headersFooters = new Map<string, HeaderFooter>();
  for (const sec of sections) {
    for (const [kind, refs] of [['header', sec.headers], ['footer', sec.footers]] as const) {
      for (const path of Object.values(refs)) {
        if (!path || headersFooters.has(path)) continue;
        const hfDoc = pkg.getXml(path);
        if (!hfDoc) continue;
        const hfWalker = new Walker(path, styles, numbering, new NumberingCounter(numbering), 1_000_000);
        hfWalker.walkBlocks(hfDoc.root, []);
        for (const tb of hfWalker.pendingTextBoxes) hfWalker.walkBlocks(tb, [], undefined, true);
        const hfParas = hfWalker.paragraphs;
        const withPage = hfParas.find((p) => p.fields.some((f) => f.type === 'PAGE'));
        headersFooters.set(path, {
          part: path,
          kind,
          paragraphs: hfParas,
          hasContent: hfParas.some((p) => !p.isEmpty),
          pageField: withPage
            ? {
                paragraph: withPage,
                align: withPage.textBox ? 'unknown' : (withPage.props.jc ?? 'left'),
                inTextBox: withPage.textBox,
              }
            : undefined,
        });
      }
    }
  }

  const settingsPart = pkg.relatedPart(mainPart, Rel.settings);
  const commentsPart = pkg.relatedPart(mainPart, Rel.comments);
  const footnotesPart = pkg.relatedPart(mainPart, Rel.footnotes);

  return {
    pkg,
    mainPart,
    styles,
    numbering,
    blocks,
    paragraphs,
    textBoxes,
    tables: walker.tables,
    sections,
    headersFooters,
    settings: readSettings(settingsPart ? pkg.getXml(settingsPart) : undefined),
    meta: readMeta(pkg),
    revisions: countRevisions(docXml.root),
    comments: countChildren(commentsPart ? pkg.getXml(commentsPart) : undefined, 'w:comment', false),
    footnotes: countChildren(footnotesPart ? pkg.getXml(footnotesPart) : undefined, 'w:footnote', true),
    altChunks: walker.altChunks,
  };
}

/** Уровень встроенного стиля «Заголовок N» (heading N): у таких абзацев Word не даёт изменить уровень структуры. */
export function builtInHeadingLevel(styles: StyleSheet, styleId: string | undefined): number | undefined {
  const m = /^heading ([1-9])$/i.exec(styles.get(styleId)?.name ?? '');
  return m ? Number(m[1]) : undefined;
}

/** Уровень, с которым Word возьмёт абзац в поле TOC \o \u: у «Заголовка N» — N, у остальных — уровень структуры. */
export function wordTocLevel(model: DocModel, p: Paragraph): number | undefined {
  const heading = builtInHeadingLevel(model.styles, p.styleId);
  if (heading) return heading;
  const lvl = p.props.outlineLvl;
  return lvl !== undefined && lvl >= 0 && lvl <= 8 ? lvl + 1 : undefined;
}

/**
 * Сегменты, которыми набран фрагмент start–end, если его можно исправить как текст: Word дробит текст
 * на фрагменты w:r, поэтому сегментов может быть несколько — но подряд, все правятся, табуляции — целиком.
 * undefined — внутри поле, чужая правка, объект или разрыв строки.
 */
export function editableSegments(p: Paragraph, start: number, end: number): TextSegment[] | undefined {
  if (start === end) {
    const seg = p.segments.find((s) => s.editable && s.node && s.start <= start && start <= s.end);
    return seg ? [seg] : undefined;
  }
  const segs = p.segments.filter((s) => s.start < end && start < s.end);
  if (!segs.length || segs[0]!.start > start || segs[segs.length - 1]!.end < end) return undefined;
  for (const [i, s] of segs.entries()) {
    if (!s.editable || (i > 0 && s.start !== segs[i - 1]!.end)) return undefined;
    if (s.tab && (start > s.start || end < s.end)) return undefined;
  }
  return segs;
}

/** Итоговое имя шрифта фрагмента для кириллицы и латиницы. */
export function runFonts(model: DocModel, run: RunInfo, text: string): string[] {
  return fontsForText(model.styles, run.props, text);
}

/** Имя вместо шрифта, когда восточноазиатский шрифт не задан и Word подставит свой. */
export const DEFAULT_EAST_ASIA_FONT = 'восточноазиатский шрифт по умолчанию';

/** Буквы, которые с пометкой w:hint="eastAsia" выводятся восточноазиатским шрифтом (ECMA-376, 17.3.2.26). */
const EAST_ASIA_HINTED = /[\p{Script=Cyrillic}\p{Script=Greek}]/u;

/** Восточноазиатский шрифт, которым Word выводит кириллицу фрагмента; undefined — пометки w:hint="eastAsia" нет. */
export function hintedEastAsiaFont(styles: StyleSheet, props: RunProps, text: string): string | undefined {
  if (props.fontHint !== 'eastAsia' || !EAST_ASIA_HINTED.test(text)) return undefined;
  return styles.eastAsiaFontName(props.fontEastAsia) ?? DEFAULT_EAST_ASIA_FONT;
}

/**
 * Гарнитуры, которыми фактически набраны буквы текста: w:ascii — латиница, w:hAnsi — кириллица,
 * а кириллица с пометкой w:hint="eastAsia" — w:eastAsia.
 */
export function fontsForText(styles: StyleSheet, props: RunProps, text: string): string[] {
  const out = new Set<string>();
  const hinted = props.fontHint === 'eastAsia';
  let ascii = false;
  let other = false;
  let eastAsia = false;
  for (const ch of text) {
    if (/\s|\d|[.,;:!?()«»"'\-–—\ufffc]/.test(ch)) continue;
    if (ch.charCodeAt(0) < 0x80) ascii = true;
    else if (hinted && EAST_ASIA_HINTED.test(ch)) eastAsia = true;
    else other = true;
  }
  const name = (ref: RunProps['fontAscii']): string => styles.fontName(ref) ?? 'Times New Roman';
  if (ascii) out.add(name(props.fontAscii));
  if (other) out.add(name(props.fontHAnsi));
  if (eastAsia) out.add(styles.eastAsiaFontName(props.fontEastAsia) ?? DEFAULT_EAST_ASIA_FONT);
  return [...out];
}
